import {useCallback} from "react";
import {NodeProps, useReactFlow, getOutgoers} from "@xyflow/react";

import {generateId} from "@/utils/index";
import {createNode} from "@/pages/workflow/NodeTypes/WorkflowNode";
import {WorkflowNodeTypes} from "@/types/node";

// this hook implements the logic for clicking a workflow node
// on workflow node click: create a new child node of the clicked node
export function useNode() {
  const {setEdges, setNodes, getNodes, getEdges, getNode} = useReactFlow();

  const addNodeByParentId = useCallback(
    (id: NodeProps["id"], blueprint: any) => {
      // Get the parent node object for positioning the new child node
      const parentNode = getNode(id);
      if (!parentNode) {
        return;
      }

      // Create a unique ID for the child node
      const childNodeId = generateId();

      // Create the child node
      blueprint.id = childNodeId;
      blueprint.position = {
        x: parentNode.position.x,
        y: parentNode.position.y,
      };
      const childNode = createNode(blueprint);

      // Create a connection from parent to child
      const childEdge = {
        id: `${parentNode.id}->${childNodeId}`,
        source: parentNode.id,
        target: childNodeId,
        type: "workflow",
      };

      // Explicitly define the types for placeholder node and edge
      let childPlaceholderNode: {
        id: string;
        position: {x: number; y: number};
        type: string;
        data: {type: string};
      } | null = null;
      let childPlaceholderEdge: {
        id: string;
        source: string;
        target: string;
        type: string;
      } | null = null;

      // If the parent node is not of type JUMP, create a placeholder node and edge
      if (parentNode.type !== WorkflowNodeTypes.JUMP) {
        const childPlaceholderId = generateId();
        childPlaceholderNode = {
          id: childPlaceholderId,
          position: {x: childNode.position.x, y: childNode.position.y},
          type: WorkflowNodeTypes.PLACEHOLDER,
          data: {type: WorkflowNodeTypes.PLACEHOLDER},
        };

        childPlaceholderEdge = {
          id: `${childNodeId}->${childPlaceholderId}`,
          source: childNodeId,
          target: childPlaceholderId,
          type: "workflow",
        };
      }

      // Remove any existing placeholders for the parent node
      const existingPlaceholders = getOutgoers(
        parentNode,
        getNodes(),
        getEdges(),
      )
        .filter(node => node.type === WorkflowNodeTypes.PLACEHOLDER)
        .map(node => node.id);

      // Update nodes (add child and optionally placeholder)
      setNodes(nodes => {
        const updatedNodes = nodes.filter(
          node => !existingPlaceholders.includes(node.id),
        );
        return childPlaceholderNode
          ? updatedNodes.concat([childNode, childPlaceholderNode])
          : updatedNodes.concat([childNode]);
      });

      // Update edges (add child edge and optionally placeholder edge)
      setEdges(edges => {
        const updatedEdges = edges.filter(
          edge => !existingPlaceholders.includes(edge.target),
        );
        return childPlaceholderEdge
          ? updatedEdges.concat([childEdge, childPlaceholderEdge])
          : updatedEdges.concat([childEdge]);
      });
    },
    [getEdges, getNode, getNodes, setEdges, setNodes],
  );

  const addSiblingById = useCallback(
    (id: NodeProps["id"], blueprint: any) => {
      const node = getNode(id);
      if (!node) return;

      const siblingNodeId = generateId();
      blueprint.id = siblingNodeId;
      blueprint.position = {x: node.position.x, y: node.position.y};
      const siblingNode = createNode(blueprint);

      const parentEdge = getEdges().find(edge => edge.target === id);
      if (!parentEdge) {
        return;
      }

      const siblingEdge = {
        id: `${parentEdge.source}->${siblingNodeId}`,
        source: parentEdge.source,
        target: siblingNodeId,
        type: "workflow",
      };

      const siblingPlaceholderId = generateId();
      const siblingPlaceholderNode = {
        id: siblingPlaceholderId,
        position: {x: siblingNode.position.x, y: siblingNode.position.y},
        type: "placeholder",
        data: {type: "placeholder"},
      };

      const siblingPlaceholderEdge = {
        id: `${siblingNodeId}->${siblingPlaceholderId}`,
        source: siblingNodeId,
        target: siblingPlaceholderId,
        type: "workflow",
      };

      setNodes(nodes => nodes.concat([siblingNode, siblingPlaceholderNode]));
      setEdges(edges => edges.concat([siblingEdge, siblingPlaceholderEdge]));
    },
    [getEdges, getNode, setEdges, setNodes],
  );

  const getNodeParent = (id: NodeProps["id"]) => {
    const node = getNode(id);
    if (!node) return;

    const parentEdge = getEdges().find(
      edge => edge.target === id && edge.type === "workflow",
    );
    if (!parentEdge) return;

    return getNode(parentEdge.source);
  };

  return {addNodeByParentId, addSiblingById, getNodeParent};
}

export default useNode;
